import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { View, StyleSheet, Keyboard, TouchableWithoutFeedback } from 'react-native';
import Spacer from 'design-system/Spacer';
import Text, { styles } from 'design-system/Text';
import { IconBoldArrow } from 'design-system/icons';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { AnimatedKeyboardAwareScrollView } from 'design-system/AnimatedComponents';
import { rem, AppView, CardView, withAnimatedNavigationHeader } from 'design-system';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import { formatAmount, formatPlural, getFractionDigits } from 'utils';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import { MoneyTextInput } from 'utils/packages/moneyInput';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { usePosition } from '../hooks';
import { isAndroid, isWeb } from '../../../constants';
import { useCurrentPrice } from '../hooks/selectors';
import { selectActiveTradingAccountId } from '../reducers/selectors';
import AssetSwitchAssetCard from '../components/AssetSwitchAssetCard';
import AssetSwitchEmptyCard from '../components/AssetSwitchEmptyCard';
import { formatNumberOfShares, getClosestDecimalCeilBias, isValidQuantityStep } from '../utils';
import useSwitchAssetOrderPreview from '../hooks/useSwitchAssetOrderPreview';
import { deactivateFundPicker, getSnapshot } from '../hooks/useFundPickerStore';
import { SHARE_QUANTITY_DECIMALS } from '../constants';
const extraHeightWihOnlyAmount = isAndroid ? rem(156) : rem(220);
const AssetSwitchScreen = withAnimatedNavigationHeader(({ navigation, scrollHandler, route }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.elements.primary,
            headerRight: () => <HelpScreenButton />,
        });
    }, [colors.elements.primary]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle, styles.contentContainer);
    const { assetId } = route.params;
    const textInputRef = useRef(null);
    const [position] = usePosition(assetId);
    const availableToSell = Number(formatNumberOfShares(position?.availableToSell || 0, false).replace(/[^0-9.]/g, ''));
    const tradingAccountId = useAppSelector(selectActiveTradingAccountId);
    const [toAssetId, setToAssetId] = useState();
    const currentPrice = useCurrentPrice(toAssetId);
    const toAssetIdAssetType = useAppSelector((store) => toAssetId ? store.invest.caches[toAssetId]?.type : undefined);
    const toAssetIdMinimumTransferUnit = useAppSelector((store) => toAssetId ? store.invest.caches[toAssetId]?.minimumTransferUnit : undefined);
    const fromAssetIdType = useAppSelector((store) => store.invest.caches[assetId]?.type);
    const fromAssetIdMinimumTransferUnit = useAppSelector((store) => store.invest.caches[assetId]?.minimumTransferUnit);
    const minFromValue = fromAssetIdType === 'ETF' ? 0.0001 : fromAssetIdMinimumTransferUnit;
    const maximumFractionDigits = minFromValue !== undefined ? getFractionDigits(minFromValue) : SHARE_QUANTITY_DECIMALS;
    const minFromText = minFromValue !== undefined ? formatNumberOfShares(minFromValue, false) : '';
    const [quantity, setQuantity] = useState('');
    const numberQuantity = Number(quantity);
    const isValidStep = isValidQuantityStep(numberQuantity, minFromValue, maximumFractionDigits);
    const [orderPreviews, fetchingOrderPreviews] = useSwitchAssetOrderPreview(assetId, toAssetId, numberQuantity <= availableToSell && isValidStep ? numberQuantity : 0, tradingAccountId);
    const latestPriceAmount = orderPreviews?.buyPreview.order.estimated.price?.value || currentPrice?.value;
    const estimatedAmount = orderPreviews?.buyPreview.order.estimated.amount;
    const min = useMemo(() => {
        if (toAssetIdAssetType) {
            const calculatedMin = 
            // eslint-disable-next-line no-nested-ternary
            toAssetIdAssetType === 'ETF'
                ? 0.0001
                : toAssetIdMinimumTransferUnit && latestPriceAmount
                    ? getClosestDecimalCeilBias(toAssetIdMinimumTransferUnit * 1.1 * latestPriceAmount)
                    : undefined;
            if (calculatedMin) {
                return Math.max(calculatedMin, 1);
            }
        }
        return undefined;
    }, [latestPriceAmount, toAssetIdAssetType, toAssetIdMinimumTransferUnit]);
    const onSelectToAssetId = useCallback((assetId) => {
        setToAssetId(assetId);
    }, []);
    useFocusEffect(useCallback(() => {
        const snapshot = getSnapshot();
        if (!snapshot.active || !snapshot.saved) {
            if (snapshot.active)
                deactivateFundPicker();
            return;
        }
        const picked = snapshot.selectedFunds.at(0);
        onSelectToAssetId(picked);
        deactivateFundPicker();
    }, [onSelectToAssetId]));
    const From = useMemo(() => <AssetSwitchAssetCard assetId={assetId} fromAssetId={assetId}/>, [assetId]);
    const To = useMemo(() => {
        if (toAssetId) {
            return <AssetSwitchAssetCard assetId={toAssetId} fromAssetId={assetId} onSelectToAssetId={onSelectToAssetId}/>;
        }
        return <AssetSwitchEmptyCard onSelectToAssetId={onSelectToAssetId} fromAssetId={assetId}/>;
    }, [assetId, onSelectToAssetId, toAssetId]);
    const hasMinError = !!(min && estimatedAmount && estimatedAmount.value < min);
    const hasMaxError = numberQuantity > availableToSell; // exceeds max
    const hasStepError = numberQuantity > 0 && !isValidStep;
    const onQuantityChange = useCallback((_formatted, extracted = '') => {
        setQuantity(extracted);
    }, []);
    const FootNote = useMemo(() => {
        if (hasMaxError) {
            return (<Text TextThin-14 Red centered>
            Only {availableToSell} {formatPlural('share', 'shares', availableToSell)} available
          </Text>);
        }
        if (hasStepError) {
            return (<Text TextThin-14 Red centered>
            Shares must be sold in multiples of {minFromText}
          </Text>);
        }
        if (hasMinError) {
            return (<Text TextThin-14 Red centered>
            Minimum order{' '}
            <Text NumbersThin-14 Red>
              {formatAmount(min, 'GBP', 2)}
            </Text>
            {estimatedAmount ? (<>
                {' (est. '}
                <Text NumbersThin-14 Red>
                  {formatAmount(estimatedAmount?.value, estimatedAmount?.currency, 2)}
                </Text>
                {')'}
              </>) : ('')}
          </Text>);
        }
        return null;
    }, [availableToSell, estimatedAmount, hasMaxError, hasMinError, hasStepError, minFromText, min]);
    const isDisabled = useMemo(() => hasMinError || hasMaxError || hasStepError || fetchingOrderPreviews || !orderPreviews, [fetchingOrderPreviews, hasMaxError, hasMinError, hasStepError, orderPreviews]);
    const handleContinue = useCallback(() => {
        Keyboard.dismiss();
        if (!isDisabled && orderPreviews && tradingAccountId && toAssetId) {
            navigation.navigate('SecclAssetSwitchOrderPreview', {
                toAssetId,
                orderPreviews,
                tradingAccountId,
                fromAssetId: assetId,
            });
        }
    }, [assetId, isDisabled, orderPreviews, toAssetId, tradingAccountId]);
    return (<AppView withFrame={false}>
        <AnimatedKeyboardAwareScrollView enableOnAndroid onScroll={scrollHandler} scrollEventThrottle={16} extraHeight={extraHeightWihOnlyAmount} keyboardShouldPersistTaps="handled" contentContainerStyle={contentContainerStyle} showsVerticalScrollIndicator={false}>
          {From}
          <Spacer h={16}/>
          <View>
            {To}
            <View style={styles.iconContainer} pointerEvents="none">
              <View style={styles.iconWrapper}>
                <IconBoldArrow alt colorKey="brand" direction="bottom"/>
              </View>
            </View>
          </View>
          <CardView noMarginBottom>
            <TouchableWithoutFeedback onPress={() => textInputRef.current?.focus()}>
              <View style={styles.amountContainer}>
                <MoneyTextInput maximumIntegerDigits={9} value={quantity} placeholder="0" numberOfLines={1} ref={textInputRef} style={styles.amountInput} onChangeText={onQuantityChange} maximumFractionalDigits={maximumFractionDigits}/>
                {FootNote}
              </View>
            </TouchableWithoutFeedback>
          </CardView>
          <View style={styles.footerTextWrapper}>
            <Text TextThin-14 centered Secondary>
              We’ll sell your investments first, then reinvest the money in a selected fund or ETF. Your switch should
              be completed within 2 working days.
            </Text>
          </View>
        </AnimatedKeyboardAwareScrollView>
        <KeyboardAvoidingButton brand title="Continue" disabled={isDisabled} onPress={handleContinue} isFetching={fetchingOrderPreviews}/>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
    title: 'Switch',
}));
const styleSet = createStyleSheets((colors) => ({
    iconContainer: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center',
        top: -rem(30),
    },
    iconWrapper: {
        width: rem(42),
        height: rem(42),
        borderWidth: rem(1),
        alignItems: 'center',
        borderRadius: rem(44),
        justifyContent: 'center',
        borderColor: colors.borders.logoStroke,
        backgroundColor: colors.background.light,
    },
    amountContainer: {
        padding: rem(16),
        justifyContent: 'center',
    },
    amountInput: {
        ...styles.Numbers_24,
        color: colors.text.primary,
        alignSelf: 'center',
        // A web input doesn't shrink to its text, so right-aligned text would sit off centre
        textAlign: isWeb ? 'center' : 'right',
        minWidth: rem(30),
        height: styles.Numbers_24.lineHeight,
        ...(isAndroid && { padding: 0 }),
    },
    contentContainer: {
        paddingTop: rem(16),
    },
    footerTextWrapper: {
        padding: rem(16),
    },
}));
export default AssetSwitchScreen;
